import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import { LocalStorageKeys } from "@/utils/local-storage-keys";
import type { PartialAccount } from "@/utils/types";

interface FeedWrapperContextProps {
  feedMeta?: FeedMeta | null;
  setFeedMeta: Dispatch<SetStateAction<FeedMeta | null>>;
  activeTab: string;
  setActiveTab: Dispatch<SetStateAction<string>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
}

type FeedMeta = {
  feedName: string;
  feedProvider: PartialAccount;
};

const FeedWrapperContext = createContext<FeedWrapperContextProps | undefined>(
  undefined,
);

export const FeedWrapperContextProvider = ({
  children,
  defaultActiveTab,
  tabLocalStorageKey,
}: {
  children: ReactNode;
  defaultActiveTab: string;
  tabLocalStorageKey: string;
}) => {
  const [feedMeta, setFeedMeta] = useLocalStorage<FeedMeta | null>(
    LocalStorageKeys.feedMeta,
    null,
  );
  const [activeTab, setActiveTab] = useLocalStorage(
    tabLocalStorageKey,
    defaultActiveTab,
  );
  const [page, setPage] = useState(0);

  return (
    <FeedWrapperContext.Provider
      value={{
        feedMeta,
        setFeedMeta,
        activeTab: activeTab ?? defaultActiveTab,
        setActiveTab,
        page,
        setPage,
      }}
    >
      {children}
    </FeedWrapperContext.Provider>
  );
};

export const useFeedWrapperContext = (): FeedWrapperContextProps => {
  const context = useContext(FeedWrapperContext);
  if (!context) {
    throw new Error(
      "useFeedMainWrapperContext must be used within a FeedMainWrapperContextProvider",
    );
  }
  return context;
};
